/**
 * useAds — React hook exposing AdsService to components.
 *
 * Provides:
 *  - `adsReady`              → boolean, true when SDK loaded + remote config says enabled
 *  - `interstitialEnabled`   → boolean, true when interstitial sub-flag is on
 *  - `rewardedEnabled`       → boolean, true when rewarded sub-flag is on
 *  - `showInterstitial()`    → call at natural transitions (game-over→lobby, etc.)
 *  - `showRewarded()`        → call on explicit user action (revive, double chips)
 *
 * All functions are safe to call even when ads are OFF — they become no-ops.
 */

import { useCallback, useSyncExternalStore } from 'react';
import {
  adsService,
  type AdPlacementType,
  type AdPlatform,
  type InterstitialHooks,
  type RewardedHooks,
} from './AdsService';

// ---------------------------------------------------------------------------
// Tiny external store so React re-renders when AdsService state changes
// ---------------------------------------------------------------------------

let snapshot = {
  ready: adsService.isEnabled(),
  interstitial: adsService.isInterstitialEnabled(),
  rewarded: adsService.isRewardedEnabled(),
  platform: adsService.getPlatform(),
};
const listeners = new Set<() => void>();

/** Call after init/config change to notify React */
export function notifyAdsStateChange(): void {
  snapshot = {
    ready: adsService.isEnabled(),
    interstitial: adsService.isInterstitialEnabled(),
    rewarded: adsService.isRewardedEnabled(),
    platform: adsService.getPlatform(),
  };
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot() {
  return snapshot;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAds() {
  const state = useSyncExternalStore(subscribe, getSnapshot);

  const showInterstitial = useCallback(
    (
      placementType: Exclude<AdPlacementType, 'reward'>,
      name: string,
      hooks?: InterstitialHooks,
    ) => {
      adsService.showInterstitial(placementType, name, hooks);
    },
    [],
  );

  const showRewarded = useCallback(
    (name: string, hooks?: RewardedHooks) => {
      adsService.showRewarded(name, hooks);
    },
    [],
  );

  return {
    adsReady: state.ready,
    interstitialEnabled: state.interstitial,
    rewardedEnabled: state.rewarded,
    platform: state.platform,
    showInterstitial,
    showRewarded,
  };
}
